import { useEffect, useRef } from "react"
import { usePromise } from "aldoo-ra/usePromise"

export default ({
  apiMethod,
  page,
  itemsPerPage,
  search,
  refreshKey,
  sort,
  onData,
  onDataRequested,
  requestFields,
  minSearchTriggerTime = 200,
}) => {
  // Use refs to track search state
  const prevSearchRef = useRef(null)
  const searchTimerRef = useRef(null)
  const executingRef = useRef(false)
  const isInitialMount = useRef(true)
  const prevRefreshKeyRef = useRef(refreshKey) // Add ref to track previous refreshKey

  const { pending, result, execute } = usePromise(() => {
    if (!apiMethod) {
      return
    }
    const payload = { page, itemsPerPage, search, sort, ...requestFields }
    return apiMethod(payload)
  })

  // Notify about loading state changes
  useEffect(() => {
    if (pending && onDataRequested) {
      onDataRequested()
    }
    // Reset executing flag when request completes
    if (!pending && executingRef.current) {
      executingRef.current = false
    }
  }, [pending])

  // Initial data load
  useEffect(() => {
    if (isInitialMount.current && execute) {
      isInitialMount.current = false
      
      // Clear any pending search timer
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
        searchTimerRef.current = null
      }
      
      executingRef.current = true
      execute()
    }
  }, [execute])

  // Handle search with debounce
  useEffect(() => {
    // Skip if currently executing to prevent loops
    if (executingRef.current) {
      return
    }
    // Convert search objects to strings for comparison
    const currentSearchStr = JSON.stringify(search)
    const prevSearchStr = JSON.stringify(prevSearchRef.current)
    const searchChanged = currentSearchStr !== prevSearchStr
    // Update the previous search reference
    prevSearchRef.current = search
    // If search hasn't changed, don't do anything
    if (!searchChanged) return
    // Clear any existing timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    // Set a new timer
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null

      // Don't execute if another request is already in progress
      if (executingRef.current) {
        return
      }

      executingRef.current = true
      execute && execute()
    }, minSearchTriggerTime || 200)
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [search, minSearchTriggerTime])

  // Handle pagination and sort changes
  const prevSortRef = useRef(sort)
  useEffect(() => {
    // Skip if currently executing to prevent loops
    if (executingRef.current) return
    // Skip if this is just initial mount (will be handled by initial load effect)
    if (isInitialMount.current) return
    if (execute) {
      executingRef.current = true
      execute()
    }
  }, [page, itemsPerPage])

  useEffect(() => {
    if (isInitialMount.current) return
    const currentSort = JSON.stringify(sort)
    const prevSort = JSON.stringify(prevSortRef.current)
    if (currentSort === prevSort) return
    prevSortRef.current = sort
    executingRef.current = true
    execute && execute()
  }, [sort])

  // Separate effect specifically for refreshKey changes
  useEffect(() => {
    // Skip if this is just initial mount
    if (isInitialMount.current) return

    // Check if refreshKey has changed
    if (refreshKey !== prevRefreshKeyRef.current) {
      prevRefreshKeyRef.current = refreshKey

      // Clear any pending search timeouts
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }

      // Force a reload even if another request is in progress
      executingRef.current = true
      execute && execute()
    }
  }, [refreshKey])

  // Handle results
  useEffect(() => {
    if (result && onData) {
      onData(result)
    }
  }, [result])

  return {
    loading: pending,
    result,
    execute,
  }
}
